/**
 * TimePeriodSelector Component
 * 
 * Pill-style selector with 3 options (Hourly/Weekly/Monthly).
 * Supports keyboard navigation (arrow keys), ARIA accessibility,
 * and integrates with the dark navy theme of the application.
 * 
 * @component
 * @example
 * <TimePeriodSelector
 *   value="weekly"
 *   onChange={(period) => setPeriod(period)}
 *   disabled={false}
 * />
 */

import { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './TimePeriodSelector.css';

/** Available time period options */
const PERIODS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'weekly', label: '7 Days' },
  { value: 'monthly', label: '30 Days' },
];

/**
 * TimePeriodSelector - Accessible pill-style time period toggle
 * 
 * Uses ARIA tablist/tab pattern for screen reader support.
 * Arrow key navigation moves focus and selection between pills.
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Currently selected period ('hourly' | 'weekly' | 'monthly')
 * @param {Function} props.onChange - Callback when period selection changes
 * @param {boolean} [props.disabled=false] - Whether the selector is disabled
 * @param {string} [props.ariaLabel='Time period'] - ARIA label for the selector group
 * @returns {JSX.Element} Pill-style period selector
 */
function TimePeriodSelector({ value, onChange, disabled = false, ariaLabel = 'Time period' }) {
  const pillRefs = useRef([]);

  /**
   * Handle keyboard navigation within the pill group
   * Supports ArrowLeft/ArrowRight for horizontal navigation,
   * Home/End for jumping to first/last option.
   */
  const handleKeyDown = useCallback((e) => {
    if (disabled) return;

    const currentIndex = PERIODS.findIndex(p => p.value === value);
    let nextIndex = currentIndex;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        nextIndex = (currentIndex + 1) % PERIODS.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        nextIndex = (currentIndex - 1 + PERIODS.length) % PERIODS.length;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = PERIODS.length - 1;
        break;
      default:
        return;
    }

    if (nextIndex !== currentIndex) {
      onChange(PERIODS[nextIndex].value);
      // Move focus to the newly selected pill
      pillRefs.current[nextIndex]?.focus();
    }
  }, [value, onChange, disabled]);

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        gap: 2,
        background: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 6,
        padding: 2,
        flexShrink: 0,
      }}
      onKeyDown={handleKeyDown}
    >
      {PERIODS.map((period, index) => {
        const isActive = value === period.value;

        return (
          <button
            key={period.value}
            ref={el => { pillRefs.current[index] = el; }}
            role="tab"
            aria-selected={isActive}
            aria-controls={`panel-${period.value}`}
            tabIndex={isActive ? 0 : -1}
            className={[
              'time-period-selector__pill',
              isActive ? 'time-period-selector__pill--active' : '',
              disabled ? 'time-period-selector__pill--disabled' : '',
            ].filter(Boolean).join(' ')}
            style={{
              border: isActive ? '1px solid rgba(255,255,255,0.9)' : '1px solid transparent',
              borderRadius: 5,
              padding: '4px 8px',
              fontSize: 10,
              fontWeight: 600,
              color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
              background: isActive ? 'rgba(255, 255, 255, 0.18)' : 'transparent',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
              minWidth: 'auto',
            }}
            onClick={() => {
              if (!disabled && !isActive) {
                onChange(period.value);
              }
            }}
            disabled={disabled}
          >
            {period.label}
          </button>
        );
      })}
    </div>
  );
}

TimePeriodSelector.propTypes = {
  /** Currently selected period value */
  value: PropTypes.oneOf(['hourly', 'weekly', 'monthly']).isRequired,
  /** Callback fired when period selection changes */
  onChange: PropTypes.func.isRequired,
  /** Whether the selector is disabled (e.g., during loading) */
  disabled: PropTypes.bool,
  /** Accessible label for the selector group */
  ariaLabel: PropTypes.string,
};

export default TimePeriodSelector;
